import { parse, format } from 'date-fns';
import type { HiAttemptFormValues, HiAttemptGetResponse, HiAttemptMedia } from './hiAttempt.schema';

export const isWithinEditableWindow = (attempt: HiAttemptGetResponse): boolean => {
  const createdAt = new Date(attempt.createdAt);
  const editableUntil = new Date(createdAt);
  editableUntil.setFullYear(editableUntil.getFullYear() + 1);
  return new Date() <= editableUntil;
};

export const getAttemptMedia = (attempt: HiAttemptGetResponse): HiAttemptMedia[] => attempt.media ?? [];

const buildMediaUrl = (media?: HiAttemptMedia['media']): string => {
  if (!media?.baseUrl || !media.path) return '';
  const base = media.baseUrl.replace(/\/$/, '');
  const path = media.path.replace(/^\//, '');
  return `${base}/${path}`;
};

export type HiAttemptImagePreview = {
  objectUrl: string;
  name: string;
  mediaId: string;
  isExisting: true;
};

export const buildAttemptImagePreviews = (attempt: HiAttemptGetResponse): HiAttemptImagePreview[] =>
  getAttemptMedia(attempt).map((m) => ({
    objectUrl: buildMediaUrl(m.media),
    name: m.media?.path?.split('/').pop() ?? m.mediaId,
    mediaId: m.mediaId,
    isExisting: true,
  }));

export const buildDefaultValues = (attempt: HiAttemptGetResponse): Partial<HiAttemptFormValues> => {
  // Convert stored HH:mm time to 12-hour format for the form
  let timeOfAttempt = attempt.timeOfAttempt ?? '';
  if (timeOfAttempt && /^\d{2}:\d{2}$/.test(timeOfAttempt)) {
    try {
      const parsed = parse(timeOfAttempt, 'HH:mm', new Date());
      timeOfAttempt = format(parsed, 'hh:mm a');
    } catch {
      // keep original if parse fails
    }
  }

  return {
    dateOfAttempt: attempt.dateOfAttempt ? new Date(attempt.dateOfAttempt) : undefined,
    timeOfAttempt,
    ownerAddressLine1: attempt.ownerAddressLine1 ?? undefined,
    ownerAddressLine2: attempt.ownerAddressLine2 ?? undefined,
    ownerCity: attempt.ownerCity ?? undefined,
    ownerState: attempt.ownerState ?? undefined,
    ownerZipCode: attempt.ownerZipCode ?? undefined,
    ownerPhone: attempt.ownerPhone ?? undefined,
    maritalStatus: attempt.maritalStatus ?? undefined,
    spouseName: attempt.spouseName ?? undefined,
    ownerOccupied: attempt.ownerOccupied ?? undefined,
    vacant: attempt.vacant ?? undefined,
    bothOwnersResideTogether: attempt.bothOwnersResideTogether ?? undefined,
    coOwnerName: attempt.coOwnerName ?? undefined,
    coOwnerAddressLine1: attempt.coOwnerAddressLine1 ?? undefined,
    coOwnerAddressLine2: attempt.coOwnerAddressLine2 ?? undefined,
    coOwnerCity: attempt.coOwnerCity ?? undefined,
    coOwnerState: attempt.coOwnerState ?? undefined,
    coOwnerZipCode: attempt.coOwnerZipCode ?? undefined,
    defendantInArmedForces: attempt.defendantInArmedForces ?? undefined,
    tenantOccupied: attempt.tenantOccupied ?? undefined,
    // Distribute top-level relative/lease/rentAmount to the first tenant only
    tenants: attempt.tenants?.map((t, i) => ({
      name: t.name,
      floor: t.floor ?? null,
      relative: i === 0 ? (attempt.relative ?? undefined) : undefined,
      lease: i === 0 ? (attempt.lease ?? undefined) : undefined,
      rentAmount: i === 0 && attempt.rentAmount != null ? Number(attempt.rentAmount) : undefined,
    })) ?? [],
    houseExterior: attempt.houseExterior as HiAttemptFormValues['houseExterior'],
    houseExteriorOther: attempt.houseExteriorOther ?? undefined,
    houseType: attempt.houseType as HiAttemptFormValues['houseType'],
    numberOfUnits: attempt.numberOfUnits,
    garage: attempt.garage as HiAttemptFormValues['garage'],
    sale: attempt.sale as HiAttemptFormValues['sale'],
    saleBy: attempt.saleBy ?? undefined,
    salePhone: attempt.salePhone ?? undefined,
    comments: attempt.comments ?? undefined,
    mediaIds: getAttemptMedia(attempt).map((m) => m.mediaId),
  };
};
